import type { Application } from '@/services/api/applications'
import type { Deployment } from '@/services/api/deployments'

export const DEFAULT_APPLICATION_KEY = 'default'

function uniqueIds(values: Array<string | null | undefined>): string[] {
  const ids: string[] = []
  const seen = new Set<string>()
  for (const value of values) {
    const id = value?.trim()
    if (!id || seen.has(id)) {
      continue
    }
    seen.add(id)
    ids.push(id)
  }
  return ids
}

function memberIdsFromManifest(
  manifest: Record<string, unknown> | undefined,
): string[] {
  const raw = manifest?.members
  if (!Array.isArray(raw)) {
    return []
  }
  return uniqueIds(
    raw.map((item) => {
      if (!item || typeof item !== 'object') {
        return null
      }
      const record = item as Record<string, unknown>
      if (String(record.kind ?? '').trim() !== 'agent') {
        return null
      }
      return String(record.agent_id ?? record.agentId ?? '')
    }),
  )
}

/** Agent ids declared on the Application (members + chat entrypoint). */
export function memberAgentIdsForApplication(app: Application): string[] {
  return uniqueIds([
    app.entrypoints?.chat?.agent_id,
    ...(app.agents ?? []).map((member) => member.agent_id),
    ...memberIdsFromManifest(app.manifest),
  ])
}

/**
 * Agents a Run user can pick under an Application (members + chat entrypoint).
 */
export function runAgentsForApplication(
  app: Application,
  deployments: Deployment[],
): Deployment[] {
  const memberIds = new Set(memberAgentIdsForApplication(app))
  const selected =
    memberIds.size === 0
      ? []
      : deployments.filter((agent) => memberIds.has(agent.id))
  const chatAgentId = app.entrypoints?.chat?.agent_id?.trim()
  return [...selected].sort((left, right) => {
    if (left.id === chatAgentId) return -1
    if (right.id === chatAgentId) return 1
    return (left.name || left.id).localeCompare(right.name || right.id)
  })
}

/**
 * Run may show an Application when people is unrestricted, when it lists this
 * user, or when its audience is organization_members (AGENT-863): the
 * Applications of an organization are listed to its members only, so every
 * signed-in reader of the list enters. The server decides (HumanMayAccess);
 * this only mirrors it for the tree and the blocking state.
 */
export function humanMayAccessApplication(
  app: Application | null | undefined,
  userId?: string | null,
): boolean {
  if (!app) {
    return false
  }
  if (app.people == null) {
    return true
  }
  const id = userId?.trim()
  if (!id) {
    return false
  }
  if (app.people_audience === 'organization_members') {
    return true
  }
  return app.people.some((person) => person.user_id === id)
}

export function applicationsVisibleInRun(
  apps: Application[],
  userId?: string | null,
): Application[] {
  return apps.filter((app) => humanMayAccessApplication(app, userId))
}

export function resolveRunChatAgentId(input: {
  app?: Application | null
  deployments: Deployment[]
  selectedAgentId?: string | null
}): string | null {
  const selected = input.selectedAgentId?.trim() || null
  if (!input.app) {
    return selected
  }
  const runAgents = runAgentsForApplication(input.app, input.deployments)
  if (selected && runAgents.some((agent) => agent.id === selected)) {
    return selected
  }
  return (
    input.app.entrypoints?.chat?.agent_id?.trim() ||
    runAgents[0]?.id ||
    selected
  )
}
